/**
 * Postcode API Service
 *
 * Provides HTTP layer for country-specific postcode lookup services.
 * Currently supports UK via postcodes.io API.
 */

import {
  PostcodeLookupResult,
  PostcodeAPIConfig,
  PostcodeValidationResult,
} from '@/types/postcode';

/**
 * UK Postcode API Implementation (postcodes.io)
 */
class UKPostcodeAPI {
  private readonly baseUrl = 'https://api.postcodes.io';

  /**
   * Validate UK postcode format
   * UK format: 1-2 letters, 1-2 digits, optional letter/digit, space, digit, 2 letters
   * Examples: SW1A 1AA, M1 1AA, CR2 6XH, DN55 1PT, W1A 0AX, EC1A 1BB
   */
  validate(postcode: string): boolean {
    const ukPattern = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
    return ukPattern.test(postcode.trim());
  }

  /**
   * Format UK postcode with proper spacing
   */
  formatPostcode(postcode: string): string {
    // Remove all whitespace and convert to uppercase
    const cleaned = postcode.replace(/\s+/g, '').toUpperCase();

    // Insert space before the last 3 characters
    if (cleaned.length >= 5) {
      return `${cleaned.slice(0, -3)} ${cleaned.slice(-3)}`;
    }

    return cleaned;
  }

  /**
   * Lookup city from UK postcode using postcodes.io API
   */
  async lookup(postcode: string): Promise<PostcodeLookupResult> {
    const formattedPostcode = this.formatPostcode(postcode);
    const url = `${this.baseUrl}/postcodes/${encodeURIComponent(formattedPostcode)}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Postcode not found');
        }
        throw new Error(`API returned status ${response.status}`);
      }

      const data = await response.json();

      if (data.status !== 200 || !data.result) {
        throw new Error('Invalid API response');
      }

      // Extract city from result
      // Priority: admin_district > parish > region
      const result = data.result;
      const city =
        result.admin_district || result.parish || result.region || null;

      if (!city) {
        throw new Error('No city information found for this postcode');
      }

      return {
        city,
        district: result.admin_district || undefined,
        region: result.region || undefined,
        formattedPostcode: result.postcode || formattedPostcode,
      };
    } catch (error) {
      if (error instanceof Error) {
        // Rethrow with more context
        if (error.message.includes('fetch')) {
          throw new Error('Network error: Unable to connect to postcode service');
        }
        throw error;
      }
      throw new Error('Unknown error during postcode lookup');
    }
  }
}

/**
 * Registry of country-specific postcode APIs
 */
const postcodeAPIRegistry: Record<string, PostcodeAPIConfig> = {
  'United Kingdom': {
    country: 'United Kingdom',
    validate: (postcode: string) => new UKPostcodeAPI().validate(postcode),
    lookup: (postcode: string) => new UKPostcodeAPI().lookup(postcode),
    formatPostcode: (postcode: string) => new UKPostcodeAPI().formatPostcode(postcode),
  },
  // Future countries can be added here:
  // 'France': {
  //   country: 'France',
  //   validate: (postcode: string) => /^\d{5}$/.test(postcode),
  //   lookup: async (postcode: string) => {
  //     // Call French postcode API
  //   },
  //   formatPostcode: (postcode: string) => postcode,
  // },
};

/**
 * Get postcode API config for a country
 */
export function getPostcodeAPIConfig(country: string): PostcodeAPIConfig | null {
  return postcodeAPIRegistry[country] || null;
}

/**
 * Validate postcode format for a specific country
 */
export function validatePostcode(
  postcode: string,
  country: string
): PostcodeValidationResult {
  const config = getPostcodeAPIConfig(country);

  if (!config) {
    return {
      isValid: false,
      formattedPostcode: postcode,
    };
  }

  const isValid = config.validate(postcode);
  const formattedPostcode = isValid ? config.formatPostcode(postcode) : postcode;

  return {
    isValid,
    formattedPostcode,
  };
}

/**
 * Lookup city from postcode for a specific country
 */
export async function lookupPostcode(
  postcode: string,
  country: string
): Promise<PostcodeLookupResult> {
  const config = getPostcodeAPIConfig(country);

  if (!config) {
    throw new Error(`Postcode lookup not supported for ${country}`);
  }

  return config.lookup(postcode);
}
